import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Description as FormIcon,
  Info as InfoIcon,
  Assessment as StatsIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useFormTemplate } from '../../hooks/useForms';
import { format } from 'date-fns';
import DevOnly from '../../components/DevOnly';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`form-tabpanel-${index}`}
    aria-labelledby={`form-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const TemplateView = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Fetch form template data
  const { data: form, isLoading, error, refetch } = useFormTemplate(formId);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Category display names
  const categoryNames = {
    'consent': 'Consent Forms',
    'treatment': 'Treatment Forms',
    'questionnaire': 'Questionnaires',
    'lab': 'Lab Forms',
    'prescription': 'Prescriptions',
    'epaper': 'Digital Forms'
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load form template: {error.message}
          <Button onClick={() => refetch()} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Form template not found
          <Button onClick={() => navigate('/forms')} size="small" sx={{ ml: 2 }}>
            Back to Forms
          </Button>
        </Alert>
      </Box>
    );
  }

  const categoryName = categoryNames[form.category] || form.category;

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/forms" color="inherit">
          Forms
        </Link>
        <Link component={RouterLink} to={`/forms/category/${form.category}`} color="inherit">
          {categoryName}
        </Link>
        <Typography color="text.primary">{form.form_name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(`/forms/category/${form.category}`)} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Typography variant="h4">
              {form.form_name}
            </Typography>
            <Chip
              icon={form.is_active ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
              label={form.is_active ? 'Active' : 'Inactive'}
              color={form.is_active ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Form ID: #{form.form_id} • Category: {categoryName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <DevOnly>
            <Button
              variant="outlined"
              startIcon={<CopyIcon />}
              onClick={() => {/* Handle duplicate */}}
            >
              Duplicate
            </Button>
          </DevOnly>
          <DevOnly>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/forms/edit/${formId}`)}
            >
              Edit Form
            </Button>
          </DevOnly>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
          <DevOnly>
            <Tab label="Statistics" icon={<StatsIcon />} iconPosition="start" />
          </DevOnly>
          <DevOnly>
            <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
          </DevOnly>
        </Tabs>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Form Details
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText 
                        primary="Form Name" 
                        secondary={form.form_name} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Form ID" 
                        secondary={`#${form.form_id}`} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Category" 
                        secondary={categoryName} 
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Status" 
                      />
                      <Chip
                        size="small"
                        label={form.is_active ? 'Active' : 'Inactive'}
                        color={form.is_active ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </ListItem>
                    <Divider />
                    <ListItem>
                      <ListItemText 
                        primary="Created Date" 
                        secondary={form.created_date ? format(new Date(form.created_date), 'PPP') : 'Unknown'} 
                      />
                    </ListItem>
                    {form.modified_date && (
                      <>
                        <Divider />
                        <ListItem>
                          <ListItemText 
                            primary="Last Modified" 
                            secondary={format(new Date(form.modified_date), 'PPP')} 
                          />
                        </ListItem>
                      </>
                    )}
                  </List>
                </CardContent>
              </Card>

              {/* Form Questions Section */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Form Questions
                  </Typography>
                  {form.questions && form.questions.length > 0 ? (
                    <List>
                      {form.questions.map((question, index) => (
                        <React.Fragment key={question.id}>
                          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Box sx={{ width: '100%', mb: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {index + 1}. {question.question_text}
                                {question.is_required && (
                                  <Chip 
                                    label="Required" 
                                    size="small" 
                                    color="error" 
                                    sx={{ ml: 1, height: 20 }}
                                  />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Type: {question.question_type}
                              </Typography>
                            </Box>
                            {/* Show field preview based on type */}
                            <Box sx={{ width: '100%', mt: 1, pl: 2 }}>
                              {question.question_type === 'text' && (
                                <TextField 
                                  disabled 
                                  fullWidth 
                                  size="small" 
                                  placeholder="Text input"
                                  variant="outlined"
                                />
                              )}
                              {question.question_type === 'textarea' && (
                                <TextField 
                                  disabled 
                                  fullWidth 
                                  multiline 
                                  rows={3} 
                                  size="small" 
                                  placeholder="Text area"
                                  variant="outlined"
                                />
                              )}
                              {question.question_type === 'date' && (
                                <TextField 
                                  disabled 
                                  type="date" 
                                  size="small" 
                                  variant="outlined"
                                />
                              )}
                              {question.question_type === 'number' && (
                                <TextField 
                                  disabled 
                                  type="number" 
                                  size="small" 
                                  placeholder="0"
                                  variant="outlined"
                                />
                              )}
                              {question.question_type === 'yes_no' && (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                  <Button variant="outlined" size="small" disabled>Yes</Button>
                                  <Button variant="outlined" size="small" disabled>No</Button>
                                </Box>
                              )}
                              {question.question_type === 'checkbox' && (
                                <FormControlLabel
                                  control={<Checkbox disabled />}
                                  label={question.question_text}
                                />
                              )}
                              {question.question_type === 'select' && (
                                <Select 
                                  disabled 
                                  fullWidth 
                                  size="small"
                                  value=""
                                  displayEmpty
                                >
                                  <MenuItem value="">Select an option</MenuItem>
                                </Select>
                              )}
                              {question.question_type === 'signature' && (
                                <Box sx={{ 
                                  border: '1px dashed #ccc', 
                                  p: 2, 
                                  borderRadius: 1,
                                  textAlign: 'center',
                                  color: 'text.secondary'
                                }}>
                                  Signature Field
                                </Box>
                              )}
                            </Box>
                          </ListItem>
                          {index < form.questions.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Alert severity="info" icon={<FormIcon />}>
                      No questions have been added to this form yet. Click "Edit Form" to add questions.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Stats
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" color="primary">
                      {form.submission_count || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Submissions
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="secondary">
                      {form.recent_submissions || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Last 30 Days
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <DevOnly>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => navigate(`/forms/submissions?form_id=${form.form_id}`)}
                      >
                        View Submissions
                      </Button>
                    </DevOnly>
                    <DevOnly>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {/* Handle preview */}}
                      >
                        Preview Form
                      </Button>
                    </DevOnly>
                    <DevOnly>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {/* Handle share */}}
                      >
                        Share Form Link
                      </Button>
                    </DevOnly>
                    <DevOnly>
                      <Button
                        fullWidth
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => {/* Handle delete */}}
                      >
                        Delete Form
                      </Button>
                    </DevOnly>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Statistics Tab */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                Detailed form statistics and analytics will be available here, including submission trends, 
                completion rates, and field-level analytics.
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info">
                Form settings and configuration options will be available here, including notifications, 
                access permissions, and integration settings.
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default TemplateView;